import { useEffect, useState } from 'react';
import { getCmsPage, type CmsPage, type CmsUserType } from '../../lib/professional-api';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

interface CmsModalProps {
  isOpen: boolean;
  onClose: () => void;
  code: string;
  userType?: CmsUserType;
  fallbackTitle?: string;
}

export function CmsModal({
  isOpen,
  onClose,
  code,
  userType = 'PROFESSIONAL',
  fallbackTitle,
}: CmsModalProps) {
  const [page, setPage] = useState<CmsPage | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setPage(null);
    getCmsPage(code, userType)
      .then((res) => {
        if (!cancelled) setPage(res.data);
      })
      .catch((err) => {
        if (!cancelled) toast.error(apiErrorMessage(err, 'Could not load content.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, code, userType]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 font-['DM_Sans',sans-serif]">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-[20px] shadow-2xl w-full max-w-[680px] max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#f0f0f0]">
          <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[20px] truncate">
            {page?.title || fallbackTitle || 'Loading…'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#f4f4f4] transition-colors text-[#3a3335] text-[22px] leading-none"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          {loading && (
            <div className="text-center text-[#908e8e] py-12">Loading…</div>
          )}
          {!loading && page && (
            <div
              className="cms-content text-[#3a3335] text-[14px] leading-[22px]"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />
          )}
        </div>
      </div>

      <style>{`
        .cms-content h1 { font-family: 'Playfair Display', sans-serif; font-weight: 800; color: #8e2a2f; font-size: 24px; line-height: 32px; margin: 0 0 12px; }
        .cms-content h2 { font-family: 'Playfair Display', sans-serif; font-weight: 700; color: #3a3335; font-size: 18px; line-height: 26px; margin: 18px 0 8px; }
        .cms-content h3 { font-weight: 700; color: #3a3335; font-size: 15px; margin: 14px 0 6px; }
        .cms-content p { margin: 0 0 10px; }
        .cms-content ul, .cms-content ol { padding-left: 20px; margin: 0 0 12px; }
        .cms-content li { margin-bottom: 4px; }
        .cms-content a { color: #8e2a2f; text-decoration: underline; }
        .cms-content strong { color: #3a3335; font-weight: 700; }
        .cms-content blockquote { border-left: 3px solid #8e2a2f; padding-left: 12px; color: #555; margin: 12px 0; font-style: italic; }
        .cms-content code { background: #f4f4f4; padding: 1px 6px; border-radius: 4px; font-size: 13px; }
      `}</style>
    </div>
  );
}
